import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Bike, Check, Store } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { CartLineRow } from "@/components/cart/CartLineRow";
import { StatusBadge } from "@/components/orders/OrderCard";
import {
  EmptyState,
  ErrorState,
  ProductSkeletonList,
} from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { branchesQuery, customerOrderQuery } from "@/lib/queries";
import {
  normalizeStatus,
  orderCustomerPaidAmount,
  orderPaymentNoticeKind,
  orderPollingInterval,
} from "@/lib/orders";
import { MapPicker } from "@/components/order-type/MapPicker";
import { formatDate, formatPrice, loc } from "@/lib/format";
import { googleMapsUrl } from "@/lib/maps";
import { cn } from "@/lib/utils";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import { useAuth } from "@/lib/auth";
import { useStorefront } from "@/lib/storefront";
import { OrderCancellation } from "@/components/orders/OrderCancellation";
import { OrderLoyalty } from "@/components/orders/OrderLoyalty";
import { OrderPaymentNotice } from "@/components/orders/OrderPaymentNotice";

export const Route = createFileRoute("/orders/$orderId")({
  component: OrderDetails,
});

function OrderDetails() {
  const { orderId } = Route.useParams();
  const { t, lang } = useI18n();
  const { ready } = useRequireAuth();
  const { data: branches } = useQuery(branchesQuery());
  const { user } = useAuth();
  const storefront = useStorefront();
  const detail = useQuery({
    ...customerOrderQuery(user?.id ?? "", orderId),
    enabled: ready,
    refetchInterval: (query) => orderPollingInterval(query.state.data),
  });
  const order = detail.data;

  const branch = branches?.find((b) => b.id === order?.branchId);
  const deliveryBeforeDiscount = order
    ? order.deliveryFee +
      (order.raw?.promotions.reduce(
        (sum, promotion) => sum + promotion.delivery_discount_minor,
        0,
      ) ?? 0)
    : 0;
  const taxIncluded =
    order &&
    order.total ===
      order.subtotal -
        order.discount +
        deliveryBeforeDiscount +
        order.serviceFee +
        (order.raw?.tip_minor ?? 0);
  const paymentNoticeKind = order
    ? orderPaymentNoticeKind(order, order.raw?.payment_summary.attemptStatus)
    : null;
  const customerPaidAmount = orderCustomerPaidAmount(order);

  return (
    <PageShell size="narrow">
      <Button asChild variant="ghost" className="mb-4">
        <Link to="/orders">
          <ArrowRight className="size-4 ltr:rotate-180" />
          {t("orders.title")}
        </Link>
      </Button>

      {!ready || detail.isPending ? (
        <ProductSkeletonList count={2} />
      ) : detail.isError ? (
        <ErrorState
          error={detail.error}
          message={t("orders.loadError")}
          onRetry={() => detail.refetch()}
        />
      ) : !order ? (
        <EmptyState
          title={t("notfound.title")}
          description={t("notfound.desc")}
        />
      ) : (
        <div className="grid gap-6">
          <header className="surface-card flex flex-wrap items-center justify-between gap-3 p-5">
            <div>
              <p className="text-sm text-muted-foreground">
                {t("success.orderNumber")}
              </p>
              <h1 className="text-2xl font-black">
                <bdi>#{order.number}</bdi>
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("orders.created")} {formatDate(order.createdAt, lang, true)}
              </p>
            </div>
            <StatusBadge status={order.status} />
          </header>

          <OrderPaymentNotice kind={paymentNoticeKind} />

          <OrderCancellation
            order={order}
            restaurantPhone={
              branch?.phones[0] ?? storefront.tenant.supportPhone
            }
          />
          <OrderLoyalty order={order} />
          {order.cancellationReason ? (
            <p className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-bold text-destructive">
              {t("orders.cancelReason")}: {order.cancellationReason}
            </p>
          ) : null}

          <section className="surface-card p-5">
            <h2 className="mb-3 text-lg font-bold">{t("orders.itemsTitle")}</h2>
            {order.lines.map((line) => {
              const item = order.raw?.items.find((item) => item.id === line.id);
              return (
                <CartLineRow
                  key={line.id}
                  line={line}
                  readOnly
                  comfortable
                  currency={order.currency}
                  totals={
                    item
                      ? {
                          gross: item.line_total_minor,
                          net: item.net_total_minor,
                        }
                      : undefined
                  }
                />
              );
            })}
            <div className="mt-4 grid gap-2 border-t border-border pt-3 text-sm">
              <Row
                label={t("cart.subtotal")}
                value={formatPrice(order.subtotal, lang, order.currency)}
              />
              {order.type === "delivery" ? (
                <Row
                  label={t("cart.deliveryFee")}
                  value={formatPrice(
                    deliveryBeforeDiscount,
                    lang,
                    order.currency,
                  )}
                />
              ) : null}
              {order.discount ? (
                <Row
                  label={
                    order.promotions[0]?.name[lang] ||
                    order.promotions[0]?.name.en ||
                    (order.loyalty?.reward
                      ? t("loyalty.cartDiscount")
                      : t("cart.discount"))
                  }
                  value={`- ${formatPrice(order.discount, lang, order.currency)}`}
                />
              ) : null}
              {order.serviceFee ? (
                <Row
                  label={t("cart.serviceFee")}
                  value={formatPrice(order.serviceFee, lang, order.currency)}
                />
              ) : null}
              {order.tax && !taxIncluded ? (
                <Row
                  label={t("cart.tax")}
                  value={formatPrice(order.tax, lang, order.currency)}
                />
              ) : null}
              {order.raw?.tip_minor ? (
                <Row
                  label={t("orders.tip")}
                  value={formatPrice(order.raw.tip_minor, lang, order.currency)}
                />
              ) : null}
              <div className="flex items-center justify-between text-base font-extrabold">
                <span>{t("cart.total")}</span>
                <span className="text-foreground">
                  {formatPrice(order.total, lang, order.currency)}
                </span>
              </div>
            </div>
          </section>

          <section className="surface-card grid gap-2 p-5 text-sm text-muted-foreground">
            <h2 className="text-lg font-bold text-foreground">
              {t("checkout.details")}
            </h2>
            <p className="flex items-center gap-2 font-bold text-foreground">
              {order.type === "delivery" ? (
                <Bike className="size-4" />
              ) : (
                <Store className="size-4" />
              )}
              {order.type === "delivery"
                ? t("menu.delivery")
                : t("menu.pickup")}
              {branch ? ` — ${loc(branch.name, lang)}` : ""}
            </p>
            <Row
              label={t("orderType.fullName")}
              value={order.raw?.customer_name ?? ""}
            />
            <Row
              label={t("orderType.phone")}
              value={order.raw?.customer_phone_e164 ?? ""}
            />
            {order.type === "pickup" && (
              <>
                {branch && (
                  <Row
                    label={t("orders.pickupAddress")}
                    value={loc(branch.address, lang)}
                  />
                )}
                <Row
                  label={t("orderType.pickupTime")}
                  value={
                    order.pickupTimeISO
                      ? formatDate(order.pickupTimeISO, lang, true)
                      : t("orders.asap")
                  }
                />
              </>
            )}
            {order.type === "delivery" && order.deliveryAddress && (
              <>
                <Row
                  label={t("orderType.street")}
                  value={order.deliveryAddress.street}
                />
                <Row
                  label={t("orderType.area")}
                  value={order.deliveryAddress.area}
                />
                <Row
                  label={t("orderType.building")}
                  value={order.deliveryAddress.building}
                />
                <Row
                  label={t("orderType.floor")}
                  value={order.deliveryAddress.floor}
                />
                <Row
                  label={t("orderType.apartment")}
                  value={order.deliveryAddress.apartment}
                />
                <Row
                  label={t("orderType.landmark")}
                  value={order.deliveryAddress.landmark}
                />
                {order.deliveryAddress.note && (
                  <p className="my-2 whitespace-pre-wrap rounded-lg bg-muted p-3">
                    {order.deliveryAddress.note}
                  </p>
                )}
                {Number.isFinite(order.deliveryAddress.lat) &&
                  Number.isFinite(order.deliveryAddress.lng) &&
                  Math.abs(order.deliveryAddress.lat) <= 90 &&
                  Math.abs(order.deliveryAddress.lng) <= 180 && (
                    <div className="mt-3 space-y-2">
                      <MapPicker
                        value={order.deliveryAddress}
                        onChange={() => undefined}
                        interactive={false}
                        height="h-60"
                      />
                      <div className="flex flex-wrap justify-between gap-2 text-xs">
                        <span dir="ltr">
                          {order.deliveryAddress.lat.toFixed(6)},{" "}
                          {order.deliveryAddress.lng.toFixed(6)}
                        </span>
                        <a
                          className="text-primary underline underline-offset-4"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={googleMapsUrl(order.deliveryAddress)}
                        >
                          {t("account.openMaps")}
                        </a>
                      </div>
                    </div>
                  )}
              </>
            )}
          </section>
          <section className="surface-card space-y-3 p-5">
            <h2 className="text-lg font-bold">{t("checkout.payment")}</h2>
            <Row
              label={t("checkout.payment")}
              value={t(
                order.paymentMethod === "online"
                  ? "checkout.onlineCard"
                  : order.type === "pickup"
                    ? "checkout.cashPickup"
                    : "checkout.cash",
              )}
            />
            <Row
              label={t("orders.paymentStatus")}
              danger={paymentNoticeKind !== null}
              value={
                order.paymentMethod === "cash" &&
                order.paymentStatus === "unpaid"
                  ? t(
                      order.status === "cancelled" ||
                        order.status === "rejected"
                        ? "orders.cashNotCollected"
                        : order.type === "pickup"
                          ? "orders.cashDuePickup"
                          : "orders.cashDueDelivery",
                    )
                  : paymentNoticeKind === "failed"
                    ? t("payment.status.failed")
                    : paymentNoticeKind === "awaiting"
                      ? t("orders.status.awaiting_payment")
                      : t(`payment.status.${order.paymentStatus}`)
              }
            />
            {order.paymentMethod === "online" && customerPaidAmount != null ? (
              <Row
                label={t("success.payment")}
                value={formatPrice(customerPaidAmount, lang, order.currency)}
              />
            ) : null}
          </section>
          {order.raw?.customer_notes && (
            <section className="surface-card p-5">
              <h2 className="mb-3 text-lg font-bold">{t("checkout.notes")}</h2>
              <p className="whitespace-pre-wrap break-words text-sm">
                {order.raw.customer_notes}
              </p>
            </section>
          )}
          {order.raw?.history.length ? (
            <section className="surface-card p-5">
              <h2 className="mb-5 text-lg font-bold">{t("orders.timeline")}</h2>
              <ol>
                {[...order.raw.history]
                  .sort((a, b) => a.created_at.localeCompare(b.created_at))
                  .map((event, index, events) => (
                    <li
                      key={event.id}
                      className="relative flex gap-3 pb-6 last:pb-0"
                    >
                      {index < events.length - 1 && (
                        <span
                          className="absolute start-3.5 top-7 bottom-0 w-px bg-border"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className="relative flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-muted"
                        aria-hidden="true"
                      >
                        <Check className="size-3.5 text-primary" />
                      </span>
                      <div className="flex flex-1 flex-wrap items-baseline justify-between gap-2 pt-1">
                        <p className="text-sm font-bold">
                          {event.from_status
                            ? t(
                                `orders.status.${normalizeStatus(event.to_status)}`,
                              )
                            : t("orders.placed")}
                        </p>
                        <time
                          dateTime={event.created_at}
                          className="text-xs text-muted-foreground"
                        >
                          {formatDate(event.created_at, lang, true)}
                        </time>
                      </div>
                    </li>
                  ))}
              </ol>
            </section>
          ) : null}
        </div>
      )}
    </PageShell>
  );
}

function Row({
  label,
  value,
  danger = false,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1 text-sm text-muted-foreground">
      <span>{label}</span>
      <bdi
        className={cn(
          "min-w-0 break-words text-end font-medium",
          danger ? "font-bold text-destructive" : "text-foreground",
        )}
      >
        {value}
      </bdi>
    </div>
  );
}
